import { useEffect, useState, useCallback } from 'react'
import { Outlet, useParams } from 'react-router-dom'
import { TopBar } from './top-bar'
import { Sidebar } from './sidebar'
import { useAuthStore } from '@/features/auth/stores/use-auth-store'
import { ProjectRoleProvider } from '@/features/auth/context/project-role-context'
import { useProjectMembershipQuery } from '@/features/auth/hooks/use-project-membership-query'

interface AppShellProps {
  activeNavKey: string
}

export function AppShell({ activeNavKey }: AppShellProps) {
  const { projectId } = useParams<{ projectId: string }>()
  const user = useAuthStore((s) => s.user)
  const setActiveProjectId = useAuthStore((s) => s.setActiveProjectId)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)

  useEffect(() => {
    if (projectId) setActiveProjectId(projectId)
  }, [projectId, setActiveProjectId])

  // Auto-collapse sidebar after a short delay on page load
  useEffect(() => {
    const t = setTimeout(() => setSidebarCollapsed(true), 700)
    return () => clearTimeout(t)
  }, [])

  // Ctrl+B / ⌘+B hotkey to toggle sidebar
  const toggleSidebar = useCallback(() => setSidebarCollapsed((c) => !c), [])
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
        e.preventDefault()
        toggleSidebar()
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [toggleSidebar])

  const { data: dbRole, isLoading: roleLoading } = useProjectMembershipQuery(
    projectId,
    user?.id
  )

  const resolvedRole = user?.isSuperUser ? 'super_user' : (dbRole ?? null)

  const userBarProps = user
    ? {
        name: user.name ?? user.email,
        email: user.email,
        initials: (user.name ?? user.email)
          .split(' ')
          .map((w) => w[0])
          .join('')
          .slice(0, 2)
          .toUpperCase(),
      }
    : undefined

  return (
    <ProjectRoleProvider role={resolvedRole} isLoading={roleLoading}>
      <div className="flex flex-col h-screen overflow-hidden">
        <TopBar
          projectId={projectId}
          showProject={true}
        />
        <div className="flex flex-1 overflow-hidden">
          <Sidebar
            activeKey={activeNavKey}
            collapsed={sidebarCollapsed}
            onToggle={toggleSidebar}
            projectId={projectId}
            role={resolvedRole}
            user={userBarProps}
          />
          <main className="flex-1 overflow-y-auto bg-background">
            <Outlet />
          </main>
        </div>
      </div>
    </ProjectRoleProvider>
  )
}
